import { useEffect, useRef, useState } from "react";
import { Outlet, Link, NavLink, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, CONTACT } from "@/data/content";

const Logo = () => (
  <Link to="/" data-testid="nav-logo" className="flex items-center">
    <img src="/images/auric-logo.png" alt="Auric Valuation" className="h-11 w-auto" />
  </Link>
);

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-white/80 backdrop-blur-xl border-b border-black/5">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 h-20 flex items-center justify-between">
        <Logo />
        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `gold-underline text-xs tracking-[0.2em] uppercase font-semibold transition-colors duration-300 ${
                  isActive ? "text-gold active" : "text-ink hover:text-gold"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-cta-button"
            className="bg-ink text-white text-xs tracking-[0.2em] uppercase font-semibold px-6 py-3 hover:bg-gold transition-colors duration-300"
          >
            Request Valuation
          </Link>
        </nav>
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden text-ink p-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      {open && (
        <nav className="lg:hidden bg-white border-t border-border px-6 py-6 flex flex-col gap-5" data-testid="mobile-nav">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm tracking-[0.15em] uppercase font-semibold ${isActive ? "text-gold" : "text-ink"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
      )}
    </header>
  );
};

const Footer = () => (
  <footer className="bg-ink text-slate-300" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-6 sm:px-12 py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
      <div className="md:col-span-5">
        <div>
          <span className="bg-white px-4 py-2 inline-flex" data-testid="footer-logo">
            <img src="/images/auric-logo.png" alt="Auric Valuation Pvt. Ltd." className="h-10 w-auto" />
          </span>
        </div>
        <p className="mt-6 text-sm leading-relaxed max-w-sm">
          Delivering reliable valuation for informed decisions. Professional valuation services
          across Rajasthan and beyond — built on transparency, accuracy and professional ethics.
        </p>
        <p className="mt-6 text-xs tracking-[0.25em] uppercase text-gold-light">Value Beyond Numbers</p>
      </div>
      <div className="md:col-span-3">
        <p className="text-xs tracking-[0.3em] uppercase text-gold-light font-semibold">Explore</p>
        <ul className="mt-6 space-y-3">
          {NAV_LINKS.map((l) => (
            <li key={l.to}>
              <Link to={l.to} data-testid={`footer-link-${l.label.toLowerCase()}`} className="text-sm hover:text-gold-light transition-colors duration-300">
                {l.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <div className="md:col-span-4">
        <p className="text-xs tracking-[0.3em] uppercase text-gold-light font-semibold">Contact</p>
        <ul className="mt-6 space-y-3 text-sm">
          <li data-testid="footer-address">{CONTACT.address}</li>
          <li>
            <a href={`mailto:${CONTACT.email}`} data-testid="footer-email" className="hover:text-gold-light transition-colors duration-300">
              {CONTACT.email}
            </a>
          </li>
          <li>
            <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} data-testid="footer-phone" className="hover:text-gold-light transition-colors duration-300">
              {CONTACT.phone}
            </a>
            <span className="mx-2 text-slate-500">·</span>
            <a href="tel:+919773347163" data-testid="footer-phone-alt" className="hover:text-gold-light transition-colors duration-300">
              {CONTACT.altPhones[0]}
            </a>
          </li>
        </ul>
      </div>
    </div>
    <div className="border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 py-6 flex flex-col sm:flex-row justify-between gap-3 text-xs text-slate-500">
        <span>© {new Date().getFullYear()} Auric Valuation Pvt. Ltd. All rights reserved.</span>
        <a href="https://www.auricvaluation.com" data-testid="footer-website" className="inline-flex items-center gap-1 hover:text-gold-light transition-colors duration-300">
          {CONTACT.website} <ArrowUpRight size={12} />
        </a>
      </div>
    </div>
  </footer>
);

const WhatsAppButton = () => (
  <a
    href="https://wa.me/919079194425?text=Hi%20Auric%20Valuation%2C%20I%20would%20like%20to%20discuss%20a%20valuation%20requirement."
    target="_blank"
    rel="noopener noreferrer"
    data-testid="whatsapp-chat-button"
    aria-label="Chat with Auric Valuation on WhatsApp"
    className="fixed bottom-6 right-6 z-50 group"
  >
    <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-20" />
    <span className="relative flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] shadow-lg shadow-black/25 group-hover:scale-110 transition-transform duration-300">
      <svg viewBox="0 0 24 24" className="h-7 w-7 fill-white" aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
      </svg>
    </span>
  </a>
);

export default function Layout() {
  const lenisRef = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.2, smoothWheel: true });
    lenisRef.current = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    lenisRef.current?.scrollTo(0, { immediate: true });
  }, [pathname]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}
